import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { toFormData } from '@/client/api/admin/lib/form';
import { badgeKeys } from './badges.query';
import type {
  AwardBadgePayload,
  CreateBadgeRulePayload,
  CreateBadgeTypePayload,
  DecideApplicationPayload,
  RevokeBadgePayload,
  UpdateBadgeRulePayload,
  UpdateBadgeTypePayload,
} from '@/client/types/badges';

const BASE = '/api/v2/admin/badges';
const TIMEOUT = { timeout: 30_000 };

const invalidateApplications = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: [...badgeKeys.all, 'applications'] });
  qc.invalidateQueries({ queryKey: badgeKeys.stats() });
};

const invalidateTypes = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: [...badgeKeys.all, 'types'] });
  qc.invalidateQueries({ queryKey: badgeKeys.stats() });
};

const invalidateRules = (qc: ReturnType<typeof useQueryClient>, btid?: number) => {
  if (btid) {
    qc.invalidateQueries({ queryKey: badgeKeys.rules(btid) });
  } else {
    qc.invalidateQueries({ queryKey: [...badgeKeys.all, 'rules'] });
  }
};

// ─── Application decisions ────────────────────────────────────────────────────

export const useApproveBadgeApplicationMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: DecideApplicationPayload) => {
    const { baid, ...rest } = payload;
    return request<unknown>(
      `${BASE}/applications/${baid}/approve`,
      'POST',
      toFormData(rest),
      TIMEOUT,
    );
  }, 'Failed to approve badge application');
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateApplications(qc),
  });
};

export const useRejectBadgeApplicationMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: DecideApplicationPayload) => {
    const { baid, ...rest } = payload;
    return request<unknown>(
      `${BASE}/applications/${baid}/reject`,
      'POST',
      toFormData(rest),
      TIMEOUT,
    );
  }, 'Failed to reject badge application');
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateApplications(qc),
  });
};

// ─── Badge types ──────────────────────────────────────────────────────────────

export const useCreateBadgeTypeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: CreateBadgeTypePayload) => {
    return request<unknown>(`${BASE}/types`, 'POST', toFormData(payload), TIMEOUT);
  }, 'Failed to create badge type');
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateTypes(qc),
  });
};

export const useUpdateBadgeTypeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: UpdateBadgeTypePayload) => {
    const { btid, ...rest } = payload;
    return request<unknown>(`${BASE}/types/${btid}`, 'PUT', toFormData(rest), TIMEOUT);
  }, 'Failed to update badge type');
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateTypes(qc),
  });
};

// ─── Rules ────────────────────────────────────────────────────────────────────

export const useCreateBadgeRuleMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: CreateBadgeRulePayload) => {
    return request<unknown>(`${BASE}/rules`, 'POST', toFormData(payload), TIMEOUT);
  }, 'Failed to create badge rule');
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { btid }) => invalidateRules(qc, btid),
  });
};

export const useUpdateBadgeRuleMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (payload: UpdateBadgeRulePayload & { btid?: number }) => {
      const { brid, btid: _btid, ...rest } = payload;
      return request<unknown>(`${BASE}/rules/${brid}`, 'PUT', toFormData(rest), TIMEOUT);
    },
    'Failed to update badge rule',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, vars) => invalidateRules(qc, vars.btid),
  });
};

export const useDeleteBadgeRuleMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { brid: number; btid?: number }) => {
    return request<unknown>(`${BASE}/rules/${args.brid}`, 'DELETE', {}, TIMEOUT);
  }, 'Failed to delete badge rule');
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { btid }) => invalidateRules(qc, btid),
  });
};

// ─── Award / revoke ───────────────────────────────────────────────────────────

export const useAwardBadgeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: AwardBadgePayload) => {
    const body = {
      ...payload,
      skip_verification:
        payload.skip_verification === false || payload.skip_verification === 0 ? 0 : 1,
    };
    return request<unknown>(`${BASE}/award`, 'POST', toFormData(body), TIMEOUT);
  }, 'Failed to award badge');
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      invalidateApplications(qc);
      invalidateTypes(qc);
    },
  });
};

export const useRevokeBadgeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: RevokeBadgePayload) => {
    return request<unknown>(`${BASE}/revoke`, 'POST', toFormData(payload), TIMEOUT);
  }, 'Failed to revoke badge');
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateApplications(qc),
  });
};
